// AUDITORIA E RELATÓRIO DO DISCADOR PREDITIVO: a observabilidade do discador,
// não o discador. Duas leituras do mesmo período, uma por endpoint:
//   - TENTATIVA A TENTATIVA (GET /v1/predictive-audit): cada discagem preditiva
//     com status, causa de desligamento, vendedor, lead e tabulação — a tabela
//     que responde "por que esta ligação não completou";
//   - NO AGREGADO (GET /v1/reports/predictive): totais do período, evolução
//     diária e o desempenho por vendedor (peso configurado, taxa de contato,
//     espera média até o atendimento, participação).
// As duas são a MESMA tela (a auditoria é um card dentro do relatório) e o
// mesmo recorte de período; separá-las daria dois arquivos que se importariam.
//
// Não entra em ./dialer.js apesar do assunto: aquele módulo é a OPERAÇÃO do
// discador (campanhas, fila, sessão do agente) e nada aqui é lido por ele nem o
// lê — os dois não se importariam. Aqui é só relatório, e sai do back por rotas
// de relatório.
//
// `CallStatus` vem emprestado de ./calls.js pelo mesmo motivo da auditoria do
// click-to-call: o filtro de status é o status da chamada, não um enum próprio.
// A tabulação da tentativa vem no payload já achatada ({ id, label, category })
// e NÃO reusa o `Disposition` de ./dispositions.js: é a projeção que o back
// manda, sem organizationId/sortOrder/active — tipar com o registro completo
// prometeria campos que não chegam.
import { api, toApiError } from "./client.js";
import type { CallStatus } from "./calls.js";

export interface PredictiveAuditItem {
  callId: string;
  campaignLeadId: string | null;
  createdAt: string;
  attemptedAt: string;
  startedAt: string | null;
  endedAt: string | null;
  duration: number | null;
  status: string;
  isAi: boolean;
  failureReason: string | null;
  hangupCause: number | null;
  phoneLast4: string | null;
  seller: { id: string; name: string; email: string } | null;
  lead: { id: string; name: string | null } | null;
  disposition: { id: string; label: string; category: string } | null;
}

export interface PredictiveAuditResponse {
  items: PredictiveAuditItem[];
  total: number;
  limit: number;
  offset: number;
}

export interface PredictiveAuditQuery {
  q?: string;
  status?: CallStatus;
  agentId?: string;
  from?: string;
  to?: string;
  limit?: number;
  offset?: number;
}

export async function getPredictiveAudit(
  params: PredictiveAuditQuery,
): Promise<PredictiveAuditResponse> {
  const { data, error } = await api<PredictiveAuditResponse>("/v1/predictive-audit", {
    query: params,
  });
  if (error) throw toApiError("GET /v1/predictive-audit", error);
  return data;
}

export interface PredictiveReportSummary {
  totals: {
    attempts: number;
    completed: number;
    noAnswer: number;
    failed: number;
    inProgress: number;
    uniqueLeads: number;
    activeSellers: number;
    // successes/successRate agora vêm do CRM (virada de etapa), não mais da
    // tabulação do vendedor — mesma troca de fonte da Task 5 no back.
    // losses/workedLeads são novos: leads marcados como perda no CRM e o
    // denominador (leads distintos com >=1 ligação preditiva no período).
    successes: number;
    losses: number;
    workedLeads: number;
    totalDurationSec: number;
    avgDurationSec: number | null;
    contactRate: number;
    successRate: number;
  };
  perDay: Array<{
    day: string;
    attempts: number;
    completed: number;
    successes: number;
    losses: number;
    workedLeads: number;
    avgDurationSec: number | null;
  }>;
  sellers: Array<{
    sellerId: string;
    name: string;
    email: string;
    configuredWeight: number;
    attempts: number;
    completed: number;
    successes: number;
    losses: number;
    workedLeads: number;
    avgDurationSec: number | null;
    /** Espera média até o atendimento (o toque), em segundos. Null quando o
     *  vendedor não teve nenhuma ligação atendida no período. */
    avgWaitSec: number | null;
    contactRate: number;
    share: number;
  }>;
  outcomes: Array<{ category: string; count: number }>;
}

export async function getPredictiveReport(
  params: { from?: string; to?: string } = {},
): Promise<PredictiveReportSummary> {
  const { data, error } = await api<PredictiveReportSummary>("/v1/reports/predictive", {
    query: params,
  });
  if (error) throw toApiError("GET /v1/reports/predictive", error);
  return data;
}
